<script>
	import { onMount } from "svelte";
	import { categories, products, menuStore } from "../stores/menuStore";
	import { complementGroups, syncGroups } from "../stores/complementsStore";
	import { 
		importMenuWithAi, 
		confirmAiMenuImport, 
		fetchProductComplementGroups, 
		syncProductComplementGroups, 
		saveProduct as apiSaveProduct, 
		toggleProductStatus as apiToggleProduct, 
		deleteProduct as apiDeleteProduct,
		saveCategory as apiSaveCategory,
		deleteCategory as apiDeleteCategory,
		reorderCategories as apiReorderCategories,
		resolveCatalogImageUrl 
	} from "../services/api";
	import ComplementsManager from "../components/ComplementsManager.svelte";
	import MasterCatalogModal from "../components/MasterCatalogModal.svelte";
	import ImageCropperModal from "../components/ImageCropperModal.svelte";
	import CategoryEditModal from "../components/menu/CategoryEditModal.svelte";
	import CategoryDrawer from "../components/menu/CategoryDrawer.svelte";
	import MenuAiImportModal from "../components/menu/MenuAiImportModal.svelte";
	import ProductEditModal from "../components/menu/ProductEditModal.svelte";
	import MenuAgileGrid from "../components/menu/MenuAgileGrid.svelte";
	import MenuAccordionList from "../components/menu/MenuAccordionList.svelte";
	import MenuSplitScreen from "../components/menu/MenuSplitScreen.svelte";
	import Icon from "../components/icons/Icon.svelte";

	// Sub-navegação: Produtos & Categorias vs Complementos Globais
	let menuActiveTab = 'products';

	// Complementos globais associados ao produto aberto no modal
	let globalGroupIdsPending = [];
	let globalGroupsLoading = false;

	export let onNotify = (msg, type) => {};

	// Estado de carregamento / sincronização
	let isRefreshing = false;

	onMount(async () => {
		await handleRefresh(true);
		syncGroups().catch(() => {});
		if ($categories.length > 0 && selectedCategoryId !== "all") {
			selectedCategoryId = $categories[0].id;
		}
	});

	async function handleRefresh(silent = false) {
		isRefreshing = true;
		try {
			await menuStore.syncFromApi();
			if (!silent && onNotify) onNotify("Cardápio atualizado com sucesso!", "success");
		} catch (err) {
			console.warn("[MenuView] Erro ao sincronizar cardápio:", err);
			if (!silent && onNotify) onNotify("Erro ao sincronizar dados do cardápio.", "error");
		} finally {
			isRefreshing = false;
		}
	}

	// Seletor de layout: 'three-columns' = Visualização Ágil, 'accordion' = Acordeão Panorâmico, 'split-screen' = Split-Screen Celular
	let activeLayout = "three-columns";
	let showLayoutMenu = false;

	// Categoria ativa selecionada ('all' = todas as categorias)
	let selectedCategoryId = "all";
	$: selectedCategory = selectedCategoryId === "all"
		? { id: "all", name: "Todas as Categorias", description: "Visualizando todos os itens do cardápio", active: true }
		: ($categories.find(c => c.id === selectedCategoryId) || $categories[0] || { id: "all", name: "Todas as Categorias" });

	$: categoryProducts = selectedCategoryId === "all"
		? $products
		: $products.filter(p => (p.categoryId === selectedCategory.id || p.category_id === selectedCategory.id));

	// Busca e Filtros de Produtos
	let searchQuery = "";
	let filterAvailability = "all"; // 'all' | 'active' | 'paused' | 'promo'

	$: filteredProducts = categoryProducts.filter(p => {
		// Filtro de busca textual
		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase().trim();
			const nameMatch = (p.name || "").toLowerCase().includes(q);
			const descMatch = (p.description || "").toLowerCase().includes(q);
			if (!nameMatch && !descMatch) return false;
		}
		// Filtro de disponibilidade e promoção
		if (filterAvailability === "active") return Boolean(p.active);
		if (filterAvailability === "paused") return !p.active;
		if (filterAvailability === "promo") return Number(p.promoPrice) > 0;
		return true;
	});

	// Métricas computadas
	$: totalProductsCount = $products.length;
	$: activeProductsCount = $products.filter(p => p.active).length;
	$: pausedProductsCount = $products.filter(p => !p.active).length;
	$: promoProductsCount = $products.filter(p => Number(p.promoPrice) > 0).length;

	// Helper para nome da categoria do produto
	function getCategoryName(catId) {
		const cat = $categories.find(c => c.id === catId);
		return cat ? cat.name : "Geral";
	}

	// ========================================================
	// HELPERS DE FORMATAÇÃO E CONTROLE DE PREÇO (PADRÃO BRASIL)
	// ========================================================
	function formatPriceInput(val) {
		if (val === null || val === undefined || val === '') return '';
		const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^0-9,\.]/g, '').replace(',', '.'));
		if (isNaN(num)) return '';
		return num.toFixed(2).replace('.', ',');
	}

	function parsePriceInput(val) {
		if (val === null || val === undefined || val === '') return 0;
		if (typeof val === 'number') return isNaN(val) ? 0 : val;
		const clean = String(val).replace(/[^0-9,\.]/g, '').replace(',', '.');
		const num = parseFloat(clean);
		return isNaN(num) ? 0 : num;
	}

	function handlePriceInput(e, field) {
		let val = e.target.value;
		// Mantém apenas dígitos e vírgula/ponto
		val = val.replace(/[^0-9,\.]/g, '');
		// Converte ponto em vírgula para manter o padrão brasileiro
		val = val.replace('.', ',');
		// Se o usuário digitou mais de uma vírgula, preserva apenas a primeira
		const parts = val.split(',');
		if (parts.length > 2) {
			val = parts[0] + ',' + parts.slice(1).join('');
		}
		// Limita a 2 casas decimais após a vírgula
		if (parts.length === 2 && parts[1].length > 2) {
			val = parts[0] + ',' + parts[1].slice(0, 2);
		}
		productForm[field] = val;
	}

	function handlePriceBlur(field) {
		const val = productForm[field];
		if (!val || String(val).trim() === '') return;
		const num = parsePriceInput(val);
		if (num > 0) {
			productForm[field] = num.toFixed(2).replace('.', ',');
		}
	}

	function stepProductPrice(field, delta) {
		const cur = parsePriceInput(productForm[field]);
		const next = Math.max(0, Math.round((cur + delta) * 100) / 100);
		productForm[field] = next > 0 ? next.toFixed(2).replace('.', ',') : (delta > 0 ? next.toFixed(2).replace('.', ',') : '');
	}

	// ========================================================
	// SUPORTE A TAMANHOS DE PIZZA (BROTO & GRANDE INDIVIDUAIS)
	// ========================================================
	function handlePizzaPriceInput(e, type) {
		let val = e.target.value;
		val = val.replace(/[^0-9,\.]/g, '');
		val = val.replace('.', ',');
		const parts = val.split(',');
		if (parts.length > 2) {
			val = parts[0] + ',' + parts.slice(1).join('');
		}
		if (parts.length === 2 && parts[1].length > 2) {
			val = parts[0] + ',' + parts[1].slice(0, 2);
		}
		if (type === 'broto') {
			productForm.pizzaBrotoPrice = val;
			productForm.price = val;
			productFormErrors.price = false;
		} else {
			productForm.pizzaGrandePrice = val;
		}
	}

	function handlePizzaPriceBlur(type) {
		const field = type === 'broto' ? 'pizzaBrotoPrice' : 'pizzaGrandePrice';
		const val = productForm[field];
		if (!val || String(val).trim() === '') return;
		const num = parsePriceInput(val);
		if (num > 0) {
			productForm[field] = num.toFixed(2).replace('.', ',');
			if (type === 'broto') {
				productForm.price = productForm[field];
			}
		}
	}

	function handlePizzaSizesToggle() {
		if (productForm.pizzaSizesEnabled) {
			if (productForm.price && !productForm.pizzaBrotoPrice) {
				productForm.pizzaBrotoPrice = productForm.price;
			}
		}
	}

	$: pizzaSizesDiffText = (() => {
		if (!productForm || !productForm.pizzaSizesEnabled) return '';
		const broto = parsePriceInput(productForm.pizzaBrotoPrice);
		const grande = parsePriceInput(productForm.pizzaGrandePrice);
		if (broto > 0 && grande > 0) {
			if (grande < broto) return 'Atenção: A Grande deve custar mais que a Broto';
			const diff = grande - broto;
			return `Adicional da Grande: + R$ ${diff.toFixed(2).replace('.', ',')}`;
		}
		return '';
	})();


	// ========================================================
	// ESTADOS E MÉTODOS DA TABELA RÁPIDA (EDIÇÃO EM MASSA)
	// ========================================================
	let quickSearch = "";
	let quickCategoryFilter = "all";
	let quickStockFilter = "all"; // 'all' | 'in_stock' | 'out_of_stock' | 'untracked'
	let quickStatusFilter = "all"; // 'all' | 'active' | 'paused'
	let quickSavingId = null;
	let quickSavedSuccessId = null;

	$: quickTableProducts = $products.filter(p => {
		const pCatId = p.categoryId || p.category_id;
		if (quickCategoryFilter !== 'all' && pCatId !== quickCategoryFilter) return false;
		if (quickStatusFilter === 'active' && !p.active) return false;
		if (quickStatusFilter === 'paused' && p.active) return false;
		if (quickStockFilter === 'out_of_stock' && (!p.track_stock || (p.stock_quantity ?? 0) > 0)) return false;
		if (quickStockFilter === 'in_stock' && (!p.track_stock || (p.stock_quantity ?? 0) <= 0)) return false;
		if (quickStockFilter === 'untracked' && p.track_stock) return false;
		if (quickSearch.trim()) {
			const q = quickSearch.trim().toLowerCase();
			const matchName = (p.name || '').toLowerCase().includes(q);
			const matchDesc = (p.description || '').toLowerCase().includes(q);
			return matchName || matchDesc;
		}
		return true;
	});

	$: quickTotalTracked = $products.filter(p => p.track_stock).length;
	$: quickTotalOut = $products.filter(p => p.track_stock && (p.stock_quantity ?? 0) <= 0).length;

	function flashQuickSaved(id) {
		quickSavedSuccessId = id;
		setTimeout(() => {
			if (quickSavedSuccessId === id) quickSavedSuccessId = null;
		}, 1800);
	}

		// Helper reativo: Identifica se o produto é uma pizza com 2 tamanhos (Broto & Grande)
	function getProductPizzaSizes(prod) {
		if (!prod) return null;
		const groups = prod.modifier_groups || prod.modifierGroups || [];
		const sizeGrp = groups.find(g => /tamanho/i.test(g.name || ''));
		if (!sizeGrp || !sizeGrp.options || sizeGrp.options.length < 2) return null;
		const optBroto = sizeGrp.options.find(o => /broto/i.test(o.name || ''));
		const optGrande = sizeGrp.options.find(o => /grande/i.test(o.name || ''));
		if (!optBroto || !optGrande) return null;
		const base = Number(prod.price || 0);
		const broto = base + Number(optBroto.price || 0);
		const grande = base + Number(optGrande.price || 0);
		return {
			isPizza: true,
			broto,
			grande,
			sizeGrpId: sizeGrp.id,
			optBrotoId: optBroto.id,
			optGrandeId: optGrande.id
		};
	}

	// Atualização inline de preço de pizza (Broto ou Grande) com validação e sincronização atômica
	async function handleQuickPizzaPriceBlur(prod, sizeType, newValue) {
		const val = Math.max(0, parsePriceInput(newValue));
		const currentSizes = getProductPizzaSizes(prod);
		if (!currentSizes) {
			return handleQuickPriceBlur(prod, newValue);
		}

		let newBroto = currentSizes.broto;
		let newGrande = currentSizes.grande;

		if (sizeType === 'broto') {
			if (val === currentSizes.broto) return;
			newBroto = val;
		} else if (sizeType === 'grande') {
			if (val === currentSizes.grande) return;
			newGrande = val;
		}

		if (newBroto <= 0 || newGrande <= 0) {
			if (onNotify) onNotify("Os preços de Broto e Grande devem ser maiores que zero.", "warning");
			return;
		}

		if (newGrande < newBroto) {
			if (onNotify) onNotify("O preço da pizza Grande não pode ser menor que o da pizza Broto.", "warning");
			return;
		}

		quickSavingId = prod.id;
		menuStore.updateProduct(prod.id, { price: newBroto });

		try {
			const apiProd = await apiSaveProduct({
				...prod,
				price: newBroto,
				category_id: prod.categoryId || prod.category_id,
				promo_price: Number(prod.promoPrice) || 0,
				pizza_sizes: {
					enabled: true,
					broto_price: newBroto,
					grande_price: newGrande
				}
			});

			if (apiProd) {
				menuStore.updateProduct(prod.id, apiProd);
			}
			flashQuickSaved(prod.id);
		} catch (err) {
			console.warn('[MenuView] Erro ao salvar preço de pizza inline:', err.message);
			if (onNotify) onNotify(`Erro ao salvar preço: ${err.message}`, "error");
		} finally {
			quickSavingId = null;
		}
	}

	async function handleQuickPriceBlur(prod, newPrice) {
		const val = Math.max(0, parsePriceInput(newPrice));
		if (val === prod.price) return;
		quickSavingId = prod.id;
		menuStore.updateProduct(prod.id, { price: val });
		try {
			await apiSaveProduct({
				...prod,
				price: val,
				category_id: prod.categoryId || prod.category_id,
				promo_price: Number(prod.promoPrice) || 0
			});
			flashQuickSaved(prod.id);
		} catch (err) {
			console.warn('[MenuView] Erro ao salvar preço inline:', err.message);
		} finally {
			quickSavingId = null;
		}
	}

	async function handleQuickPromoBlur(prod, newPromo) {
		const val = Math.max(0, parsePriceInput(newPromo));
		if (val === prod.promoPrice) return;
		quickSavingId = prod.id;
		menuStore.updateProduct(prod.id, { promoPrice: val });
		try {
			await apiSaveProduct({
				...prod,
				promo_price: val,
				category_id: prod.categoryId || prod.category_id
			});
			flashQuickSaved(prod.id);
		} catch (err) {
			console.warn('[MenuView] Erro ao salvar preço promocional inline:', err.message);
		} finally {
			quickSavingId = null;
		}
	}

	async function handleQuickStockToggle(prod) {
		const nextTrack = !prod.track_stock;
		const nextQty = nextTrack && (prod.stock_quantity === undefined || prod.stock_quantity === null || prod.stock_quantity === 0) ? 10 : (prod.stock_quantity ?? 0);
		quickSavingId = prod.id;
		menuStore.updateProduct(prod.id, { track_stock: nextTrack, stock_quantity: nextQty });
		try {
			await apiSaveProduct({
				...prod,
				track_stock: nextTrack,
				stock_quantity: nextQty,
				category_id: prod.categoryId || prod.category_id,
				promo_price: Number(prod.promoPrice) || 0
			});
			flashQuickSaved(prod.id);
			if (onNotify) onNotify(`Estoque de "${prod.name}" ${nextTrack ? 'ativado' : 'desativado'}.`, "info");
		} catch (err) {
			console.warn('[MenuView] Erro ao alternar controle de estoque:', err.message);
		} finally {
			quickSavingId = null;
		}
	}

	async function handleQuickStockChange(prod, newQty) {
		const sanitizedQty = Math.max(0, parseInt(newQty, 10) || 0);
		if (sanitizedQty === prod.stock_quantity) return;
		quickSavingId = prod.id;
		const wasAutoPaused = prod.auto_paused_by_stock;
		const shouldReactivate = wasAutoPaused && sanitizedQty > 0;
		const nextActive = shouldReactivate ? true : prod.active;

		menuStore.updateProduct(prod.id, {
			stock_quantity: sanitizedQty,
			active: nextActive,
			auto_paused_by_stock: shouldReactivate ? false : prod.auto_paused_by_stock
		});

		try {
			await apiSaveProduct({
				...prod,
				stock_quantity: sanitizedQty,
				track_stock: true,
				active: nextActive,
				category_id: prod.categoryId || prod.category_id,
				promo_price: Number(prod.promoPrice) || 0
			});
			flashQuickSaved(prod.id);
			if (shouldReactivate && onNotify) {
				onNotify(`"${prod.name}" reabastecido (${sanitizedQty} un.) e reativado automaticamente!`, "success");
			}
		} catch (err) {
			console.warn('[MenuView] Erro ao atualizar quantidade de estoque:', err.message);
		} finally {
			quickSavingId = null;
		}
	}

	function handleQuickStockStep(prod, delta) {
		const current = prod.stock_quantity ?? 0;
		const next = Math.max(0, current + delta);
		handleQuickStockChange(prod, next);
	}

	// Estado do Drawer Lateral de Configurações da Categoria
	let showCategoryDrawer = false;

	// Menu contextual de categoria (⋮)
	let openCategoryMenuId = null;

	function toggleCategoryMenu(catId) {
		openCategoryMenuId = openCategoryMenuId === catId ? null : catId;
	}

	function openDrawerForCategory(cat) {
		if (cat.id === "all") return;
		selectedCategoryId = cat.id;
		showCategoryDrawer = true;
		openCategoryMenuId = null;
	}

	function handleGlobalClick(e) {
		if (!e.target.closest(".cat-menu-wrapper")) {
			openCategoryMenuId = null;
		}
		if (!e.target.closest(".layout-dropdown-wrapper")) {
			showLayoutMenu = false;
		}
	}

	// Modais de Criação / Edição
	let showCategoryModal = false;
	let editingCategoryId = null;
	let categoryForm = { name: "", icon: "utensils", description: "", image: "" };

	let showProductModal = false;
	let editingProductId = null;
	let showMasterCatalogModal = false;
	let catalogOriginInfo = null;
	let productForm = {
		categoryId: "",
		name: "",
		description: "",
		price: "",
		promoPrice: "",
		image: "",
		modifierGroups: [],
		trackStock: false,
		stockQuantity: 0
	};
	let productFormErrors = { name: false, price: false, category: false };

	// Modal de Novo Grupo de Modificador
	let newModifierGroup = {
		name: "",
		required: false,
		min: 1,
		max: 1,
		options: []
	};
	let newOptionName = "";
	let newOptionPrice = "";

	// ================= UPLOAD E ENQUADRAMENTO DE IMAGEM =================
	let showCropperModal = false;
	let rawImageToCrop = "";
	let cropperTargetType = "product";

	function handleImageUpload(e, targetType) {
		const file = e.target.files && e.target.files[0];
		if (!file) return;

		if (file.size > 8 * 1024 * 1024) {
			if (onNotify) onNotify("A imagem selecionada é muito pesada (máximo 8MB).", "warning");
			return;
		}

		const reader = new FileReader();
		reader.onload = (event) => {
			rawImageToCrop = event.target.result;
			cropperTargetType = targetType;
			showCropperModal = true;
		};
		reader.readAsDataURL(file);
		e.target.value = "";
	}

	function handleCroppedPhoto(croppedBase64) {
		showCropperModal = false;
		if (cropperTargetType === "product") {
			productForm = { ...productForm, image: croppedBase64 };
		} else if (cropperTargetType === "category") {
			categoryForm = { ...categoryForm, image: croppedBase64 };
		} else if (cropperTargetType === "quickCategory") {
			menuStore.updateCategory(selectedCategory.id, { image: croppedBase64 });
			apiSaveCategory({ id: selectedCategory.id, image: croppedBase64 }).then(() => {
				if (onNotify) onNotify(`Foto da categoria "${selectedCategory.name}" atualizada!`, "success");
			}).catch(err => {
				console.warn('[MenuView] Erro ao sincronizar foto da categoria com o backend:', err.message);
			});
		}
		rawImageToCrop = "";
	}

	function handleUseOriginalPhoto() {
		showCropperModal = false;
		if (cropperTargetType === "product") {
			productForm = { ...productForm, image: rawImageToCrop };
		} else if (cropperTargetType === "category") {
			categoryForm = { ...categoryForm, image: rawImageToCrop };
		} else if (cropperTargetType === "quickCategory") {
			menuStore.updateCategory(selectedCategory.id, { image: rawImageToCrop });
			apiSaveCategory({ id: selectedCategory.id, image: rawImageToCrop }).then(() => {
				if (onNotify) onNotify(`Foto da categoria "${selectedCategory.name}" atualizada!`, "success");
			}).catch(err => {
				console.warn('[MenuView] Erro ao sincronizar foto da categoria com o backend:', err.message);
			});
		}
		rawImageToCrop = "";
	}

	function handleCancelCrop() {
		showCropperModal = false;
		rawImageToCrop = "";
	}

	function removeImage(targetType) {
		if (targetType === "product") {
			productForm = { ...productForm, image: "" };
		} else if (targetType === "category") {
			categoryForm = { ...categoryForm, image: "" };
		}
	}

	let isSavingCategory = false;

	async function handleQuickCategoryPhotoUpload(e) {
		const file = e.target.files && e.target.files[0];
		if (!file || selectedCategory.id === "all") return;
		const reader = new FileReader();
		reader.onload = (event) => {
			rawImageToCrop = event.target.result;
			cropperTargetType = "quickCategory";
			showCropperModal = true;
		};
		reader.readAsDataURL(file);
		e.target.value = "";
	}

	async function removeQuickCategoryPhoto() {
		if (selectedCategory.id === "all") return;
		menuStore.updateCategory(selectedCategory.id, { image: "" });
		try {
			await apiSaveCategory({ id: selectedCategory.id, image: "" });
			if (onNotify) onNotify(`Foto removida com sucesso.`, "info");
		} catch (err) {
			console.warn('[MenuView] Erro ao remover foto da categoria no backend:', err.message);
		}
	}

	// ================= AÇÕES DE CATEGORIA =================
	function openNewCategoryModal() {
		editingCategoryId = null;
		categoryForm = { name: "", icon: "utensils", description: "", image: "" };
		showCategoryModal = true;
	}

	function openEditCategoryModal(cat) {
		editingCategoryId = cat.id;
		categoryForm = {
			name: cat.name,
			icon: cat.icon || "utensils",
			description: cat.description || "",
			image: cat.image || ""
		};
		showCategoryModal = true;
	}

	async function saveCategory() {
		if (!categoryForm.name.trim()) {
			if (onNotify) onNotify("Por favor, informe o nome da categoria.", "warning");
			return;
		}

		isSavingCategory = true;
		try {
			if (editingCategoryId) {
				const updatedCat = await apiSaveCategory({
					id: editingCategoryId,
					name: categoryForm.name.trim(),
					icon: categoryForm.icon,
					description: categoryForm.description,
					image: categoryForm.image
				});
				menuStore.updateCategory(editingCategoryId, updatedCat || categoryForm);
				if (onNotify) onNotify(`Categoria "${categoryForm.name}" atualizada com sucesso!`, "success");
			} else {
				const createdCat = await apiSaveCategory({
					name: categoryForm.name.trim(),
					icon: categoryForm.icon || "🍽️",
					description: categoryForm.description,
					image: categoryForm.image
				});
				const newCat = {
					id: createdCat.id,
					name: createdCat.name,
					icon: createdCat.icon || categoryForm.icon || "🍽️",
					description: createdCat.description || categoryForm.description || "",
					image: createdCat.image || categoryForm.image || "",
					active: Boolean(createdCat.active !== 0 && createdCat.active !== false),
					order: createdCat.display_order || ($categories.length + 1)
				};
				menuStore.addCategory(newCat);
				selectedCategoryId = createdCat.id;
				if (onNotify) onNotify(`Nova categoria "${categoryForm.name}" criada com sucesso!`, "success");
			}
			showCategoryModal = false;
		} catch (err) {
			console.error('[MenuView] Erro ao salvar categoria:', err);
			if (onNotify) onNotify(`Erro ao salvar categoria no servidor: ${err.message || 'Falha de comunicação'}`, "error");
		} finally {
			isSavingCategory = false;
		}
	}

	async function deleteCategory(cat) {
		if (confirm(`Tem certeza que deseja excluir a categoria "${cat.name}" e todos os seus produtos?`)) {
			try {
				await apiDeleteCategory(cat.id);
				menuStore.deleteCategory(cat.id);
				if (selectedCategoryId === cat.id) {
					selectedCategoryId = "all";
				}
				if (onNotify) onNotify(`Categoria "${cat.name}" excluída.`, "warning");
			} catch (err) {
				console.error('[MenuView] Erro ao excluir categoria:', err);
				if (onNotify) onNotify(`Erro ao excluir categoria no servidor: ${err.message || 'Falha de comunicação'}`, "error");
			}
		}
	}

	async function toggleCategory(cat) {
		const newActive = !cat.active;
		menuStore.toggleCategoryStatus(cat.id);
		const status = newActive ? "ativada" : "pausada";
		if (onNotify) onNotify(`Categoria "${cat.name}" ${status}. A IA do WhatsApp foi atualizada.`, "info");
		try {
			await apiSaveCategory({ id: cat.id, active: newActive ? 1 : 0 });
		} catch (err) {
			console.warn('[MenuView] Erro ao atualizar status da categoria no backend:', err.message);
		}
	}

	async function moveCategory(cat, dir) {
		menuStore.moveCategory(cat.id, dir);
		try {
			const sorted = $categories.map((c, idx) => ({ id: c.id, order: idx + 1 }));
			await apiReorderCategories(sorted);
		} catch (err) {
			console.warn('[MenuView] Erro ao sincronizar reordenação de categorias:', err.message);
		}
	}

	// ================= AÇÕES DE PRODUTO =================
	function openNewProductModal() {
		editingProductId = null;
		catalogOriginInfo = null;
		globalGroupIdsPending = [];
		productFormErrors = { name: false, price: false, category: false };
		const defaultCatId = (selectedCategory && selectedCategory.id !== "all")
			? selectedCategory.id
			: ($categories[0] ? $categories[0].id : "");

		productForm = {
			categoryId: defaultCatId,
			name: "",
			description: "",
			price: "",
			promoPrice: "",
			image: "",
			modifierGroups: [],
			trackStock: false,
			stockQuantity: 0,
			pizzaSizesEnabled: false,
			pizzaBrotoPrice: "",
			pizzaGrandePrice: ""
		};
		showProductModal = true;
	}

	async function openEditProductModal(prod) {
		editingProductId = prod.id;
		catalogOriginInfo = null;
		globalGroupIdsPending = [];
		globalGroupsLoading = true;
		productFormErrors = { name: false, price: false, category: false };

		let hasSizes = false;
		let brotoVal = "";
		let grandeVal = "";

		const sizeGrp = (prod.modifier_groups || prod.modifierGroups || []).find(g => /tamanho/i.test(g.name || ''));
		if (sizeGrp && sizeGrp.options && sizeGrp.options.length >= 2) {
			const optBroto = sizeGrp.options.find(o => /broto/i.test(o.name || ''));
			const optGrande = sizeGrp.options.find(o => /grande/i.test(o.name || ''));
			if (optBroto && optGrande) {
				hasSizes = true;
				const basePriceNum = Number(prod.price || 0);
				const brotoNum = basePriceNum + Number(optBroto.price || 0);
				const grandeNum = basePriceNum + Number(optGrande.price || 0);
				brotoVal = formatPriceInput(brotoNum);
				grandeVal = formatPriceInput(grandeNum);
			}
		}

		productForm = {
			categoryId: prod.categoryId || prod.category_id || "",
			name: prod.name,
			description: prod.description || "",
			price: prod.price !== undefined && prod.price !== null ? formatPriceInput(prod.price) : "",
			promoPrice: prod.promoPrice ? formatPriceInput(prod.promoPrice) : "",
			image: prod.image || "",
			modifierGroups: JSON.parse(JSON.stringify(prod.modifierGroups || [])),
			trackStock: Boolean(prod.track_stock),
			stockQuantity: prod.stock_quantity !== undefined && prod.stock_quantity !== null
				? Math.max(0, parseInt(prod.stock_quantity, 10))
				: 0,
			pizzaSizesEnabled: hasSizes,
			pizzaBrotoPrice: brotoVal,
			pizzaGrandePrice: grandeVal
		};
		showProductModal = true;

		try {
			const groups = await fetchProductComplementGroups(prod.id);
			globalGroupIdsPending = (groups || []).map(g => g.id);
		} catch (err) {
			console.warn('[MenuView] Erro ao carregar complementos globais do produto:', err.message);
		} finally {
			globalGroupsLoading = false;
		}
	}

	function handleSelectFromMasterCatalog(event) {
		const { product, options, matchedCategoryId } = event.detail;
		if (!product) return;

		// Se estiver editando e já tiver campos preenchidos, preserva integridade
		if (options.name && product.nome) {
			productForm.name = product.nome;
		}
		if (options.image && product.foto) {
			productForm.image = resolveCatalogImageUrl(product.foto);
		}
		if (options.category && matchedCategoryId) {
			productForm.categoryId = matchedCategoryId;
		}
		if (options.suggestedPrice && product.preco_sugerido) {
			productForm.price = formatPriceInput(product.preco_sugerido);
		}

		catalogOriginInfo = {
			name: product.nome,
			brand: product.marca || "",
			sku: product.sku || "",
			suggestedPrice: product.preco_sugerido
		};

		if (onNotify) {
			onNotify(`Dados de "${product.nome}" pré-preenchidos do Catálogo Mestre. Revise e salve quando quiser!`, "info");
		}
	}

	function toggleGlobalGroupPending(groupId) {
		if (globalGroupIdsPending.includes(groupId)) {
			globalGroupIdsPending = globalGroupIdsPending.filter(id => id !== groupId);
		} else {
			globalGroupIdsPending = [...globalGroupIdsPending, groupId];
		}
	}

	function addOptionToNewGroup() {
		if (!newOptionName.trim()) return;
		newModifierGroup.options = [
			...newModifierGroup.options,
			{
				id: `opt-${Date.now()}`,
				name: newOptionName.trim(),
				price: Number(newOptionPrice) || 0
			}
		];
		newOptionName = "";
		newOptionPrice = "";
	}

	function addModifierGroupToProduct() {
		if (!newModifierGroup.name.trim() || newModifierGroup.options.length === 0) {
			if (onNotify) onNotify("Informe o nome do grupo e adicione pelo menos uma opção.", "warning");
			return;
		}
		productForm.modifierGroups = [
			...productForm.modifierGroups,
			{
				id: `mod-${Date.now()}`,
				name: newModifierGroup.name.trim(),
				required: Boolean(newModifierGroup.required),
				min: Number(newModifierGroup.min) || 0,
				max: Number(newModifierGroup.max) || 1,
				options: newModifierGroup.options
			}
		];
		newModifierGroup = { name: "", required: false, min: 1, max: 1, options: [] };
	}

	function removeModifierGroup(index) {
		productForm.modifierGroups = productForm.modifierGroups.filter((_, i) => i !== index);
	}

	let isSavingProduct = false;

	async function saveProduct() {
		let parsedPrice = parsePriceInput(productForm.price);
		const hasName = Boolean(productForm.name && productForm.name.trim());
		const hasPrice = parsedPrice > 0;
		const hasCategory = Boolean(productForm.categoryId && productForm.categoryId !== "all");

		productFormErrors = {
			name: !hasName,
			price: !hasPrice,
			category: !hasCategory
		};

		if (!hasName) {
			if (onNotify) onNotify("Por favor, preencha o nome do produto.", "warning");
			const el = document.getElementById("product-form-name");
			if (el) el.focus();
			return;
		}

		if (!hasCategory) {
			if (onNotify) onNotify("Por favor, selecione uma categoria válida para o produto.", "warning");
			const el = document.getElementById("product-form-category");
			if (el) el.focus();
			return;
		}

		if (productForm.pizzaSizesEnabled) {
			const brotoNum = parsePriceInput(productForm.pizzaBrotoPrice);
			const grandeNum = parsePriceInput(productForm.pizzaGrandePrice);

			if (brotoNum <= 0) {
				if (onNotify) onNotify("Por favor, informe um preço válido para o tamanho Broto.", "warning");
				const el = document.getElementById("pizza-broto-price");
				if (el) el.focus();
				return;
			}
			if (grandeNum <= 0) {
				if (onNotify) onNotify("Por favor, informe um preço válido para o tamanho Grande.", "warning");
				const el = document.getElementById("pizza-grande-price");
				if (el) el.focus();
				return;
			}
			if (grandeNum < brotoNum) {
				if (onNotify) onNotify("O preço da pizza Grande não pode ser menor que o da pizza Broto.", "warning");
				const el = document.getElementById("pizza-grande-price");
				if (el) el.focus();
				return;
			}
			parsedPrice = brotoNum;
		} else {
			if (!hasPrice) {
				if (onNotify) onNotify("Por favor, informe um preço base válido maior que zero.", "warning");
				const el = document.getElementById("product-form-price");
				if (el) el.focus();
				return;
			}
		}

		isSavingProduct = true;
		const pendingGroupIds = [...globalGroupIdsPending];
		const trackStock = Boolean(productForm.trackStock);
		const existingProd = editingProductId ? $products.find(p => p.id === editingProductId) : null;
		const stockQuantity = trackStock 
			? Math.max(0, parseInt(productForm.stockQuantity || 0, 10))
			: (existingProd && existingProd.stock_quantity !== undefined ? existingProd.stock_quantity : 0);

		const parsedPromoPrice = parsePriceInput(productForm.promoPrice);

		const pizzaSizesPayload = productForm.pizzaSizesEnabled
			? {
				enabled: true,
				broto_price: parsePriceInput(productForm.pizzaBrotoPrice),
				grande_price: parsePriceInput(productForm.pizzaGrandePrice)
			}
			: {
				enabled: false
			};

		const productPayload = {
			...productForm,
			category_id: productForm.categoryId,
			price: parsedPrice,
			promo_price: parsedPromoPrice,
			promoPrice: parsedPromoPrice,
			track_stock: trackStock,
			stock_quantity: stockQuantity,
			pizza_sizes: pizzaSizesPayload
		};

		try {
			if (editingProductId) {
				const wasAutoPaused = Boolean(existingProd && existingProd.auto_paused_by_stock && !existingProd.active);
				const shouldReactivate = wasAutoPaused && (trackStock ? stockQuantity > 0 : true);

				const updates = {
					...productPayload,
					...(shouldReactivate ? { active: true, auto_paused_by_stock: false } : {})
				};

				if (shouldReactivate) {
					await apiToggleProduct(editingProductId).catch(() => {});
				}

				const apiProd = await apiSaveProduct({
					id: editingProductId,
					...updates,
					category_id: productForm.categoryId,
					promo_price: Number(productForm.promoPrice) || 0
				});

				menuStore.updateProduct(editingProductId, updates);

				if (pendingGroupIds.length > 0) {
					await syncProductComplementGroups(editingProductId, pendingGroupIds).catch(err => {
						console.error('[MenuView] Erro ao salvar grupos de complementos:', err.message);
					});
				}
				syncGroups().catch(() => {});
				if (onNotify) onNotify(`Produto "${productForm.name}" salvo com sucesso!`, "success");
			} else {
				const newProdData = {
					...productPayload,
					auto_paused_by_stock: false
				};

				const apiProd = await apiSaveProduct({
					...newProdData,
					category_id: productForm.categoryId,
					promo_price: Number(productForm.promoPrice) || 0
				});

				const savedProd = {
					...newProdData,
					id: (apiProd && apiProd.id) ? apiProd.id : undefined,
					active: Boolean(apiProd && apiProd.active !== 0 && apiProd.active !== false)
				};

				menuStore.addProduct(savedProd);

				if (apiProd && apiProd.id && pendingGroupIds.length > 0) {
					await syncProductComplementGroups(apiProd.id, pendingGroupIds).catch(() => {});
				}
				syncGroups().catch(() => {});
				if (onNotify) onNotify(`Produto "${productForm.name}" cadastrado com sucesso!`, "success");
			}
			showProductModal = false;
		} catch (err) {
			console.error('[MenuView] Erro ao salvar produto:', err);
			if (onNotify) onNotify(`Erro ao salvar produto no servidor: ${err.message || 'Falha de comunicação'}`, "error");
		} finally {
			isSavingProduct = false;
		}
	}

	function toggleProduct(prod) {
		menuStore.toggleProductStatus(prod.id);
		const status = !prod.active ? "disponível" : "pausado";
		if (onNotify) onNotify(`"${prod.name}" marcado como ${status}.`, "info");
		apiToggleProduct(prod.id).catch(err => {
			console.warn('[MenuView] Erro ao alternar status do produto no backend:', err.message);
		});
	}

	async function deleteProduct(prod) {
		if (confirm(`Excluir o produto "${prod.name}"?`)) {
			try {
				await apiDeleteProduct(prod.id);
				menuStore.deleteProduct(prod.id);
				if (onNotify) onNotify(`Produto excluído com sucesso.`, "warning");
			} catch (err) {
				console.error('[MenuView] Erro ao excluir produto:', err);
				if (onNotify) onNotify(`Erro ao excluir produto no servidor: ${err.message || 'Falha de comunicação'}`, "error");
			}
		}
	}

	function handleSyncAI() {
		const aiContent = menuStore.exportMenuForAI();
		console.log("[MataFome AI Menu Sync]", aiContent);
		if (onNotify) onNotify("Cardápio sincronizado instantaneamente com o robô de IA do WhatsApp!", "success", 5);
	}

	// Mock do Celular para Layout 3
	let phoneCart = [];
	function addToPhoneCart(prod) {
		phoneCart = [...phoneCart, prod];
		if (onNotify) onNotify(`Item adicionado na sacola de teste do cliente!`, "info", 2);
	}

	// ========================================================
	// MODAL: IMPORTAÇÃO INTELIGENTE DE CARDÁPIO COM IA
	// ========================================================
	let showAiImportModal = false;
	let aiImportStep = 1; // 1 = Upload, 2 = Processando, 3 = Revisão
	let aiFileBase64 = "";
	let aiFileMimeType = "";
	let aiFileName = "";
	let aiFilePreview = "";
	let isAiLoading = false;
	let aiErrorMessage = "";
	let aiExtractedData = { totalCategories: 0, totalProducts: 0, categories: [] };
	let isSavingAiMenu = false;

	function openAiImportModal() {
		showAiImportModal = true;
		aiImportStep = 1;
		aiFileBase64 = "";
		aiFileMimeType = "";
		aiFileName = "";
		aiFilePreview = "";
		aiErrorMessage = "";
		aiExtractedData = { totalCategories: 0, totalProducts: 0, categories: [] };
		syncGroups().catch(() => {});
	}

	function handleAiFileSelect(e) {
		const file = (e.target.files && e.target.files[0]) || (e.dataTransfer && e.dataTransfer.files[0]);
		if (!file) return;

		if (file.size > 25 * 1024 * 1024) {
			if (onNotify) onNotify("O arquivo selecionado é muito grande (máximo 25MB).", "warning");
			return;
		}

		aiFileName = file.name;
		aiFileMimeType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');

		const reader = new FileReader();
		reader.onload = (event) => {
			aiFileBase64 = event.target.result;
			if (aiFileMimeType.startsWith('image/')) {
				aiFilePreview = event.target.result;
			} else {
				aiFilePreview = "";
			}
		};
		reader.readAsDataURL(file);
	}

	function handleAiDrop(e) {
		e.preventDefault();
		if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			handleAiFileSelect(e);
		}
	}

	function autoMatchComplementGroups(categories, groups) {
		if (!Array.isArray(categories) || !Array.isArray(groups) || groups.length === 0) return;

		for (const cat of categories) {
			if (!cat.complementGroupIds) cat.complementGroupIds = [];
			const catNameLower = (cat.name || '').toLowerCase();
			const prodNamesLower = (cat.products || []).map(p => (p.name || '').toLowerCase()).join(' ');
			const fullText = `${catNameLower} ${prodNamesLower}`;

			// Se for bebida ou refrigerante, não associa complementos de comida
			const isDrinkCategory = /\b(bebida|bebidas|refrigerante|refrigerantes|suco|sucos|cerveja|cervejas|vinho|drink|agua|água|chopp)\b/i.test(catNameLower);
			if (isDrinkCategory) continue;

			for (const g of groups) {
				const gNameLower = (g.name || '').toLowerCase();

				// 1. Pizza -> Bordas, Massas, Queijo Extra
				const isPizzaCat = /\b(pizza|pizzas|calzone|broto)\b/i.test(fullText);
				const isPizzaGroup = /\b(borda|bordas|massa|massas|recheio|recheios|pizza)\b/i.test(gNameLower);
				if (isPizzaCat && isPizzaGroup) {
					if (!cat.complementGroupIds.includes(g.id)) cat.complementGroupIds.push(g.id);
					continue;
				}

				// 2. Burger / Lanches -> Turbine seu Burger, Adicionais, Molhos, Ponto da Carne, Queijo
				const isBurgerCat = /\b(burger|hamburguer|hambúrguer|lanche|lanches|smash|artesanal|sanduiche|sanduíche)\b/i.test(fullText);
				const isBurgerGroup = /\b(burger|hamburguer|hambúrguer|lanche|turbine|adicional|adicionais|molho|molhos|ponto|carne|queijo|bacon)\b/i.test(gNameLower);
				if (isBurgerCat && isBurgerGroup) {
					if (!cat.complementGroupIds.includes(g.id)) cat.complementGroupIds.push(g.id);
					continue;
				}

				// 3. Açaí / Sorvetes -> Acompanhamentos, Frutas, Caldas, Coberturas
				const isAcaiCat = /\b(acai|açaí|sorvete|sorvetes|gelato|copo|tigela)\b/i.test(fullText);
				const isAcaiGroup = /\b(acai|açaí|acompanhamento|acompanhamentos|fruta|frutas|calda|caldas|cobertura|coberturas|adicional|adicionais)\b/i.test(gNameLower);
				if (isAcaiCat && isAcaiGroup) {
					if (!cat.complementGroupIds.includes(g.id)) cat.complementGroupIds.push(g.id);
					continue;
				}

				// 4. Pastel -> Borda, Molho, Recheio
				const isPastelCat = /\b(pastel|pasteis|pastéis)\b/i.test(fullText);
				const isPastelGroup = /\b(pastel|borda|molho|adicional)\b/i.test(gNameLower);
				if (isPastelCat && isPastelGroup) {
					if (!cat.complementGroupIds.includes(g.id)) cat.complementGroupIds.push(g.id);
					continue;
				}

				// 5. Match direto por nome (ex: grupo "Molhos" e categoria com "Molhos" ou "Adicionais")
				if (catNameLower.includes(gNameLower) || gNameLower.includes(catNameLower)) {
					if (!cat.complementGroupIds.includes(g.id)) cat.complementGroupIds.push(g.id);
				}
			}
		}
	}

	async function runAiMenuExtraction() {
		if (!aiFileBase64) {
			if (onNotify) onNotify("Por favor, selecione uma foto ou PDF do cardápio.", "warning");
			return;
		}

		isAiLoading = true;
		aiErrorMessage = "";
		aiImportStep = 2;

		try {
			const result = await importMenuWithAi(aiFileBase64, aiFileMimeType);
			if (result && result.categories && result.categories.length > 0) {
				// Executa Auto-Vínculo Inteligente por afinidade com os grupos existentes da loja
				autoMatchComplementGroups(result.categories, $complementGroups);
				aiExtractedData = result;
				aiImportStep = 3;
			} else {
				throw new Error("A IA não conseguiu identificar itens legíveis. Tente uma foto com iluminação melhor ou PDF nítido.");
			}
		} catch (err) {
			aiErrorMessage = err.message || "Erro ao processar cardápio com IA.";
			aiImportStep = 1;
		} finally {
			isAiLoading = false;
		}
	}

	function toggleCatComplementGroup(cat, groupId) {
		if (!cat.complementGroupIds) cat.complementGroupIds = [];
		const idx = cat.complementGroupIds.indexOf(groupId);
		if (idx >= 0) {
			cat.complementGroupIds.splice(idx, 1);
		} else {
			cat.complementGroupIds.push(groupId);
		}
		aiExtractedData = { ...aiExtractedData };
	}

	function removeAiExtractedProduct(catIdx, prodIdx) {
		aiExtractedData.categories[catIdx].products.splice(prodIdx, 1);
		if (aiExtractedData.categories[catIdx].products.length === 0) {
			aiExtractedData.categories.splice(catIdx, 1);
		}
		aiExtractedData.totalProducts = aiExtractedData.categories.reduce((acc, c) => acc + c.products.length, 0);
		aiExtractedData.totalCategories = aiExtractedData.categories.length;
		aiExtractedData = { ...aiExtractedData };
	}

	function removeAiExtractedCategory(catIdx) {
		aiExtractedData.categories.splice(catIdx, 1);
		aiExtractedData.totalProducts = aiExtractedData.categories.reduce((acc, c) => acc + c.products.length, 0);
		aiExtractedData.totalCategories = aiExtractedData.categories.length;
		aiExtractedData = { ...aiExtractedData };
	}

	async function confirmAndSaveAiMenu() {
		if (!aiExtractedData.categories || aiExtractedData.categories.length === 0) {
			if (onNotify) onNotify("Nenhum produto restante para salvar.", "warning");
			return;
		}

		isSavingAiMenu = true;
		try {
			const res = await confirmAiMenuImport(aiExtractedData.categories, aiExtractedData.complementGroups || []);
			await menuStore.syncFromApi();
			await syncGroups().catch(() => {});

			if (aiExtractedData.categories.length > 0) {
				const firstCatName = String(aiExtractedData.categories[0].name || '').toLowerCase();
				const found = $categories.find(c => c.name.toLowerCase() === firstCatName);
				if (found) selectedCategoryId = found.id;
			}

			if (onNotify) onNotify(res.message || "Cardápio importado e cadastrado com sucesso!", "success", 5);
			showAiImportModal = false;
		} catch (err) {
			if (onNotify) onNotify("Erro ao salvar produtos: " + err.message, "error");
		} finally {
			isSavingAiMenu = false;
		}
	}
</script>

<svelte:window on:click={handleGlobalClick} />

<div class="menu-page">
	<!-- HEADER SAAS MODERNO -->
	<header class="menu-header">
		<div class="header-left">
			<div class="header-title-row">
				<div class="brand-icon-box">
					<Icon name="utensils" size={20} color="#FF6A00" />
				</div>
				<div>
					<div class="header-heading-line">
						<h1>Cardápio & Produtos</h1>
						<span class="badge-role">Catálogo Oficial</span>
					</div>
					<p class="header-subtitle">Gerencie produtos, categorias, preços e complementos</p>
				</div>
			</div>

			<!-- Métricas Rápidas no Header -->
			<div class="header-metrics">
				<div class="metric-chip" title="Total de produtos cadastrados">
					<Icon name="package" size={14} color="#64748B" />
					<span class="metric-val">{totalProductsCount}</span>
					<span class="metric-lbl">produtos</span>
				</div>
				<div class="metric-chip" title="Total de categorias cadastradas">
					<Icon name="tag" size={14} color="#64748B" />
					<span class="metric-val">{$categories.length}</span>
					<span class="metric-lbl">categorias</span>
				</div>
				<div class="metric-chip active-chip" title="Produtos disponíveis no cardápio">
					<span class="dot-live"></span>
					<span class="metric-val text-emerald">{activeProductsCount}</span>
					<span class="metric-lbl">ativos</span>
				</div>
			</div>
		</div>

		<!-- Ações Superiores -->
		<div class="header-actions">
			<!-- Botão de Atualização Rápida -->
			<button
				type="button"
				class="btn-header-refresh {isRefreshing ? 'spinning' : ''}"
				on:click={() => handleRefresh(false)}
				disabled={isRefreshing}
				title="Sincronizar dados do banco"
			>
				<Icon name="refresh" size={15} />
				<span>{isRefreshing ? 'Atualizando...' : 'Atualizar'}</span>
			</button>

			<!-- Dropdown Seletor de Modo de Visualização -->
			<div class="layout-dropdown-wrapper">
				<button
					type="button"
					class="btn-layout-dropdown"
					on:click|stopPropagation={() => (showLayoutMenu = !showLayoutMenu)}
					title="Alternar modo de visualização"
				>
					<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<rect x="3" y="3" width="7" height="7"></rect>
						<rect x="14" y="3" width="7" height="7"></rect>
						<rect x="14" y="14" width="7" height="7"></rect>
						<rect x="3" y="14" width="7" height="7"></rect>
					</svg>
					<span class="layout-label">
						{#if activeLayout === "three-columns"}Visualização Ágil{/if}
						{#if activeLayout === "accordion"}Acordeão Panorâmico{/if}
						{#if activeLayout === "split-screen"}Split-Screen Celular{/if}
					</span>
					<span class="chevron-arrow">▾</span>
				</button>

				{#if showLayoutMenu}
					<div class="layout-menu-popover" on:click|stopPropagation>
						<div class="menu-popover-title">Modos de Visualização</div>
						<button
							type="button"
							class="layout-menu-item {activeLayout === 'three-columns' ? 'active' : ''}"
							on:click={() => { activeLayout = 'three-columns'; showLayoutMenu = false; }}
						>
							<div class="item-title">1. Visualização Ágil</div>
							<div class="item-sub">Categorias e produtos em colunas dinâmicas</div>
						</button>
						<button
							type="button"
							class="layout-menu-item {activeLayout === 'accordion' ? 'active' : ''}"
							on:click={() => { activeLayout = 'accordion'; showLayoutMenu = false; }}
						>
							<div class="item-title">2. Acordeão Panorâmico</div>
							<div class="item-sub">Todas as seções abertas em lista vertical</div>
						</button>
						<button
							type="button"
							class="layout-menu-item {activeLayout === 'split-screen' ? 'active' : ''}"
							on:click={() => { activeLayout = 'split-screen'; showLayoutMenu = false; }}
						>
							<div class="item-title">3. Split-Screen Celular</div>
							<div class="item-sub">Simulador ao vivo do cardápio do cliente</div>
						</button>
					</div>
				{/if}
			</div>

			<!-- Botão Digitalizar Cardápio por Foto/PDF -->
			<button type="button" class="btn-import-ai" on:click={openAiImportModal} title="Ler cardápio impresso ou PDF da gráfica com IA">
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
					<polyline points="17 8 12 3 7 8"></polyline>
					<line x1="12" y1="3" x2="12" y2="15"></line>
				</svg>
				<span>Digitalizar Cardápio</span>
			</button>
		</div>
	</header>

	<!-- SUB-NAVEGAÇÃO: Produtos vs Complementos -->
	<div class="menu-subnav">
		<button
			type="button"
			class="subnav-tab {menuActiveTab === 'products' ? 'active' : ''}"
			on:click={() => menuActiveTab = 'products'}
		>
			<Icon name="package" size={15} />
			<span>Produtos & Categorias</span>
		</button>
		<button
			type="button"
			class="subnav-tab {menuActiveTab === 'complements' ? 'active' : ''}"
			on:click={() => menuActiveTab = 'complements'}
		>
			<Icon name="layers" size={15} />
			<span>Complementos Globais</span>
		</button>
		<button
			type="button"
			class="subnav-tab {menuActiveTab === 'quick-table' ? 'active' : ''}"
			on:click={() => menuActiveTab = 'quick-table'}
		>
			<Icon name="table" size={15} />
			<span>Tabela Rápida</span>
			{#if quickTotalOut > 0}
				<span class="subnav-badge-alert" title="{quickTotalOut} produtos esgotados">{quickTotalOut}</span>
			{/if}
		</button>
	</div>

	{#if menuActiveTab === 'products'}
	<!-- ======================================================= -->
	<!-- LAYOUTS DE VISUALIZAÇÃO DO CARDÁPIO (DESACOPLADOS)       -->
	<!-- ======================================================= -->
	{#if activeLayout === "three-columns"}
		<MenuAgileGrid
			categories={$categories}
			products={$products}
			bind:selectedCategoryId={selectedCategoryId}
			selectedCategory={selectedCategory}
			categoryProducts={categoryProducts}
			totalProductsCount={totalProductsCount}
			bind:openCategoryMenuId={openCategoryMenuId}
			on:selectCategory={(e) => (selectedCategoryId = e.detail)}
			on:openNewCategoryModal={openNewCategoryModal}
			on:openEditCategoryModal={(e) => openEditCategoryModal(e.detail)}
			on:openDrawerForCategory={(e) => openDrawerForCategory(e.detail)}
			on:toggleCategory={(e) => toggleCategory(e.detail)}
			on:moveCategory={(e) => moveCategory(e.detail.cat, e.detail.dir)}
			on:deleteCategory={(e) => deleteCategory(e.detail)}
			on:openCategoryDrawer={() => (showCategoryDrawer = true)}
			on:openNewProductModal={openNewProductModal}
			on:toggleProduct={(e) => toggleProduct(e.detail)}
			on:openEditProductModal={(e) => openEditProductModal(e.detail)}
			on:deleteProduct={(e) => deleteProduct(e.detail)}
			on:toggleCategoryMenu={(e) => toggleCategoryMenu(e.detail)}
		/>
	{:else if activeLayout === "accordion"}
		<MenuAccordionList
			categories={$categories}
			products={$products}
			bind:selectedCategoryId={selectedCategoryId}
			on:openNewCategoryModal={openNewCategoryModal}
			on:openNewProductModal={openNewProductModal}
			on:toggleCategory={(e) => toggleCategory(e.detail)}
			on:openEditCategoryModal={(e) => openEditCategoryModal(e.detail)}
			on:toggleProduct={(e) => toggleProduct(e.detail)}
			on:openEditProductModal={(e) => openEditProductModal(e.detail)}
		/>
	{:else if activeLayout === "split-screen"}
		<MenuSplitScreen
			categories={$categories}
			bind:selectedCategoryId={selectedCategoryId}
			selectedCategory={selectedCategory}
			categoryProducts={categoryProducts}
			bind:phoneCart={phoneCart}
			on:openNewCategoryModal={openNewCategoryModal}
			on:openEditProductModal={(e) => openEditProductModal(e.detail)}
			on:toggleProduct={(e) => toggleProduct(e.detail)}
			on:updateProductPrice={(e) => menuStore.updateProduct(e.detail.prodId, { price: e.detail.price })}
			on:addToPhoneCart={(e) => addToPhoneCart(e.detail)}
			on:clearPhoneCart={() => (phoneCart = [])}
		/>
	{/if}
	{:else if menuActiveTab === 'quick-table'}
		<!-- TABELA RÁPIDA DE PRODUTOS (EDIÇÃO EM MASSA) -->
		<div class="quick-table-page">
			<!-- Header / Barra de Filtros e Resumo -->
			<div class="quick-table-header-card">
				<div class="quick-table-headline">
					<div class="quick-headline-meta">
						<div class="quick-headline-title">
							<Icon name="table" size={20} color="#FF6A00" />
							<h3>Tabela Rápida de Produtos</h3>
							<span class="quick-counter-pill">{quickTableProducts.length} de {$products.length} produtos</span>
						</div>
						<p class="quick-headline-sub">Altere preços, estoque e disponibilidade em tempo real sem precisar abrir produtos um por um.</p>
					</div>

					<!-- Resumo Métricas -->
					<div class="quick-metrics-row">
						<div class="quick-stat-badge">
							<span class="stat-label">Com estoque ativo</span>
							<strong class="stat-val">{quickTotalTracked}</strong>
						</div>
						{#if quickTotalOut > 0}
							<div class="quick-stat-badge out-alert">
								<span class="stat-label">Esgotados</span>
								<strong class="stat-val">{quickTotalOut}</strong>
							</div>
						{/if}
					</div>
				</div>

				<!-- Filtros Rápidos -->
				<div class="quick-filters-bar">
					<div class="quick-search-box">
						<Icon name="search" size={15} color="#94A3B8" />
						<input
							type="text"
							placeholder="Buscar produto por nome..."
							bind:value={quickSearch}
						/>
						{#if quickSearch}
							<button type="button" class="btn-clear-search" on:click={() => quickSearch = ""}>
								<Icon name="x" size={13} />
							</button>
						{/if}
					</div>

					<div class="quick-filter-selects">
						<div class="quick-select-group">
							<label for="filter-quick-cat">Categoria:</label>
							<select id="filter-quick-cat" bind:value={quickCategoryFilter}>
								<option value="all">Todas ({$products.length})</option>
								{#each $categories as cat}
									<option value={cat.id}>{cat.name} ({$products.filter(p => (p.categoryId === cat.id || p.category_id === cat.id)).length})</option>
								{/each}
							</select>
						</div>

						<div class="quick-select-group">
							<label for="filter-quick-stock">Estoque:</label>
							<select id="filter-quick-stock" bind:value={quickStockFilter}>
								<option value="all">Todos os tipos</option>
								<option value="in_stock">Com estoque ({quickTotalTracked - quickTotalOut})</option>
								<option value="out_of_stock">Esgotados ({quickTotalOut})</option>
								<option value="untracked">Ilimitado ({$products.length - quickTotalTracked})</option>
							</select>
						</div>

						<div class="quick-select-group">
							<label for="filter-quick-status">Status:</label>
							<select id="filter-quick-status" bind:value={quickStatusFilter}>
								<option value="all">Todos ({$products.length})</option>
								<option value="active">Somente Ativos ({$products.filter(p => p.active).length})</option>
								<option value="paused">Somente Pausados ({$products.filter(p => !p.active).length})</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			<!-- Tabela Responsiva de Produtos -->
			<div class="quick-table-wrapper">
				{#if quickTableProducts.length === 0}
					<div class="quick-empty-state">
						<div class="empty-icon-wrap">
							<Icon name="search" size={32} color="#94A3B8" />
						</div>
						<h4>Nenhum produto encontrado</h4>
						<p>Tente ajustar os filtros ou a busca acima para encontrar os itens desejados.</p>
						{#if quickSearch || quickCategoryFilter !== 'all' || quickStockFilter !== 'all' || quickStatusFilter !== 'all'}
							<button type="button" class="btn-reset-filters" on:click={() => { quickSearch = ''; quickCategoryFilter = 'all'; quickStockFilter = 'all'; quickStatusFilter = 'all'; }}>
								Limpar filtros
							</button>
						{/if}
					</div>
				{:else}
					<table class="quick-products-table">
						<thead>
							<tr>
								<th class="th-prod">Produto</th>
								<th class="th-cat">Categoria</th>
								<th class="th-price">Preço Normal (R$)</th>
								<th class="th-price">Preço Promo (R$)</th>
								<th class="th-track">Controlar Estoque?</th>
								<th class="th-stock">Qtd. Disponível</th>
								<th class="th-status">Status no Cardápio</th>
								<th class="th-actions">Ações</th>
							</tr>
						</thead>
						<tbody>
							{#each quickTableProducts as prod (prod.id)}
								<tr class="quick-row {quickSavedSuccessId === prod.id ? 'row-just-saved' : ''} {!prod.active ? 'row-paused' : ''}">
									<!-- Produto: Imagem + Nome + Descrição -->
									<td class="td-prod">
										<div class="prod-identity">
											<div class="prod-thumb-mini">
												{#if prod.image}
													<img src={prod.image} alt={prod.name} />
												{:else}
													<Icon name="utensils" size={16} color="#94A3B8" />
												{/if}
											</div>
											<div class="prod-name-meta">
												<strong class="prod-name-text" title={prod.name}>{prod.name}</strong>
												{#if prod.description}
													<span class="prod-desc-text" title={prod.description}>{prod.description}</span>
												{/if}
											</div>
										</div>
									</td>

									<!-- Categoria -->
									<td class="td-cat">
										<span class="cat-chip">{getCategoryName(prod.categoryId || prod.category_id)}</span>
									</td>

									<!-- Preço Normal (Adaptativo: Produtos Regulares e Pizzas Broto/Grande) -->
									<td class="td-price">
										{#if getProductPizzaSizes(prod)}
											{@const pizzaSizes = getProductPizzaSizes(prod)}
											<div class="pizza-sizes-cell-stack">
												<!-- Tamanho Broto (4 fatias) -->
												<div class="pizza-size-input-row" title="Preço do tamanho Broto (4 fatias)">
													<span class="size-label-tag tag-broto">Broto</span>
													<div class="price-input-wrapper price-input-mini">
														<span class="currency-prefix">R$</span>
														<input
															type="text"
															inputmode="decimal"
															class="inline-price-input"
															value={formatPriceInput(pizzaSizes.broto)}
															on:change={(e) => handleQuickPizzaPriceBlur(prod, 'broto', e.target.value)}
														/>
													</div>
												</div>
												<!-- Tamanho Grande (8 fatias) -->
												<div class="pizza-size-input-row" title="Preço do tamanho Grande (8 fatias)">
													<span class="size-label-tag tag-grande">Grande</span>
													<div class="price-input-wrapper price-input-mini">
														<span class="currency-prefix">R$</span>
														<input
															type="text"
															inputmode="decimal"
															class="inline-price-input"
															value={formatPriceInput(pizzaSizes.grande)}
															on:change={(e) => handleQuickPizzaPriceBlur(prod, 'grande', e.target.value)}
														/>
													</div>
												</div>
											</div>
										{:else}
											<div class="price-input-wrapper">
												<span class="currency-prefix">R$</span>
												<input
													type="text"
													inputmode="decimal"
													class="inline-price-input"
													value={formatPriceInput(prod.price)}
													on:change={(e) => handleQuickPriceBlur(prod, e.target.value)}
												/>
											</div>
										{/if}
									</td>

									<!-- Preço Promo -->
									<td class="td-price">
										<div class="price-input-wrapper">
											<span class="currency-prefix">R$</span>
											<input
												type="text"
												inputmode="decimal"
												class="inline-price-input promo"
												placeholder="0,00"
												value={prod.promoPrice ? formatPriceInput(prod.promoPrice) : ''}
												on:change={(e) => handleQuickPromoBlur(prod, e.target.value)}
											/>
										</div>
									</td>

									<!-- Toggle de Controle de Estoque -->
									<td class="td-track">
										<label class="switch-control switch-mini" title="Ativar ou desativar controle de estoque">
											<input
												type="checkbox"
												checked={prod.track_stock}
												on:change={() => handleQuickStockToggle(prod)}
											/>
											<span class="slider-switch"></span>
										</label>
									</td>

									<!-- Quantidade de Estoque -->
									<td class="td-stock">
										{#if prod.track_stock}
											<div class="stock-cell-content">
												<div class="stock-stepper-wrap">
													<button
														type="button"
														class="btn-stepper-mini"
														disabled={(prod.stock_quantity ?? 0) <= 0}
														on:click={() => handleQuickStockStep(prod, -1)}
														title="Reduzir 1 unidade"
													>
														<Icon name="minus" size={11} />
													</button>
													<input
														type="number"
														min="0"
														step="1"
														class="inline-stock-input {(prod.stock_quantity ?? 0) <= 0 ? 'stock-zero' : ''}"
														value={prod.stock_quantity ?? 0}
														on:keydown={(e) => {
															if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
														}}
														on:change={(e) => handleQuickStockChange(prod, e.target.value)}
													/>
													<button
														type="button"
														class="btn-stepper-mini"
														on:click={() => handleQuickStockStep(prod, 1)}
														title="Adicionar 1 unidade"
													>
														<Icon name="plus" size={11} />
													</button>
												</div>

												{#if (prod.stock_quantity ?? 0) <= 0}
													<span class="stock-badge-esgotado">Esgotado</span>
												{/if}
											</div>
										{:else}
											<span class="stock-unlimited-pill" title="Este produto não tem limite de estoque">
												Ilimitado (—)
											</span>
										{/if}
									</td>

									<!-- Status Ativo/Pausado -->
									<td class="td-status">
										<button
											type="button"
											class="quick-status-btn {prod.active ? 'active' : 'paused'}"
											on:click={() => toggleProduct(prod)}
											title={prod.active ? "Clique para pausar item" : "Clique para reativar no cardápio"}
										>
											{#if prod.active}
												<span class="status-dot green"></span>
												<span>Disponível</span>
											{:else}
												<span class="status-dot gray"></span>
												<span>Pausado</span>
											{/if}
										</button>
									</td>

									<!-- Ações -->
									<td class="td-actions">
										<div class="row-actions-group">
											{#if quickSavedSuccessId === prod.id}
												<span class="saved-flash-pill">Salvo ✓</span>
											{/if}
											<button
												type="button"
												class="btn-quick-edit-full"
												on:click={() => openEditProductModal(prod)}
												title="Abrir edição completa (fotos, complementos e detalhes)"
											>
												<Icon name="edit" size={13} />
												<span>Editar</span>
											</button>
										</div>
									</td>
								</tr>
							{/each}
						</tbody>
					</table>
				{/if}
			</div>
		</div>
	{:else}
		<!-- CENTRAL DE COMPLEMENTOS GLOBAIS -->
		<div class="complements-tab-container">
			<ComplementsManager {onNotify} />
		</div>
	{/if}
</div>

<!-- ======================================================= -->
<!-- MODAIS MODULARIZADOS: CATEGORIA, PRODUTO E IA CARDÁPIO  -->
<!-- ======================================================= -->
<CategoryEditModal
	show={showCategoryModal}
	editingCategoryId={editingCategoryId}
	bind:categoryForm={categoryForm}
	isSavingCategory={isSavingCategory}
	on:close={() => (showCategoryModal = false)}
	on:save={saveCategory}
	on:imageUpload={(e) => handleImageUpload(e.detail, 'category')}
	on:removeImage={() => removeImage('category')}
/>

<ProductEditModal
	show={showProductModal}
	editingProductId={editingProductId}
	bind:productForm={productForm}
	productFormErrors={productFormErrors}
	categories={$categories}
	complementGroups={$complementGroups}
	globalGroupIdsPending={globalGroupIdsPending}
	catalogOriginInfo={catalogOriginInfo}
	pizzaSizesDiffText={pizzaSizesDiffText}
	isSavingProduct={isSavingProduct}
	on:close={() => (showProductModal = false)}
	on:save={saveProduct}
	on:imageUpload={(e) => handleImageUpload(e.detail, 'product')}
	on:removeImage={() => removeImage('product')}
	on:openMasterCatalog={() => (showMasterCatalogModal = true)}
	on:clearOrigin={() => (catalogOriginInfo = null)}
	on:toggleGlobalGroup={(e) => toggleGlobalGroupPending(e.detail)}
	on:stepPrice={(e) => stepProductPrice(e.detail.field, e.detail.delta)}
	on:priceInput={(e) => handlePriceInput(e.detail.e, e.detail.field)}
	on:priceBlur={(e) => handlePriceBlur(e.detail)}
	on:pizzaSizesToggle={handlePizzaSizesToggle}
	on:pizzaPriceInput={(e) => handlePizzaPriceInput(e.detail.e, e.detail.type)}
	on:pizzaPriceBlur={(e) => handlePizzaPriceBlur(e.detail)}
/>

<MenuAiImportModal
	show={showAiImportModal}
	aiImportStep={aiImportStep}
	aiErrorMessage={aiErrorMessage}
	aiFileBase64={aiFileBase64}
	aiFilePreview={aiFilePreview}
	aiFileName={aiFileName}
	bind:aiExtractedData={aiExtractedData}
	complementGroups={$complementGroups}
	isSavingAiMenu={isSavingAiMenu}
	on:close={() => (showAiImportModal = false)}
	on:aiDrop={handleAiDrop}
	on:aiFileSelect={handleAiFileSelect}
	on:changeFile={() => { aiImportStep = 1; aiFileBase64 = ""; aiFilePreview = ""; aiFileName = ""; }}
	on:processAiImport={runAiMenuExtraction}
	on:removeCategory={(e) => removeAiExtractedCategory(e.detail)}
	on:removeProduct={(e) => removeAiExtractedProduct(e.detail.catIdx, e.detail.prodIdx)}
	on:toggleCatComplementGroup={(e) => toggleCatComplementGroup(e.detail.cat, e.detail.groupId)}
	on:confirmAndSave={confirmAndSaveAiMenu}
	on:backToStep1={() => (aiImportStep = 1)}
/>

<MasterCatalogModal
	bind:open={showMasterCatalogModal}
	currentForm={productForm}
	storeCategories={$categories}
	on:select={handleSelectFromMasterCatalog}
	on:close={() => (showMasterCatalogModal = false)}
/>

<ImageCropperModal
	bind:show={showCropperModal}
	imageSrc={rawImageToCrop}
	title={cropperTargetType === 'category' || cropperTargetType === 'quickCategory' ? 'Enquadrar Foto da Categoria' : 'Enquadrar Foto do Produto'}
	onCrop={handleCroppedPhoto}
	onUseOriginal={handleUseOriginalPhoto}
	onCancel={handleCancelCrop}
/>

<style>
	/* ========================================================
	   BASE E ESTRUTURA DA PÁGINA
	   ======================================================== */
	.menu-page {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		background: var(--neutral-bg, #F8FAFC);
		overflow: hidden;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	}

	/* ========================================================
	   HEADER SAAS MODERNO
	   ======================================================== */
	.menu-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 14px 24px;
		background: #FFFFFF;
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		gap: 16px;
		flex-shrink: 0;
	}

	/* ========================================================
	   SUB-NAVEGAÇÃO (Produtos vs Complementos)
	   ======================================================== */
	.menu-subnav {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 10px 24px;
		background: #FFFFFF;
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		flex-shrink: 0;
	}

	.subnav-tab {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 8px 16px;
		border: 1px solid transparent;
		background: transparent;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 600;
		color: #64748B;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.subnav-tab:hover {
		color: #1E293B;
		background: #F1F5F9;
	}

	.subnav-tab.active {
		color: #FF6A00;
		background: #FFF7ED;
		border-color: #FED7AA;
	}

	.complements-tab-container {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		width: 100%;
		scrollbar-width: thin;
		scrollbar-color: #CBD5E1 transparent;
	}

	.complements-tab-container::-webkit-scrollbar {
		width: 8px;
	}

	.complements-tab-container::-webkit-scrollbar-track {
		background: #F1F5F9;
		border-radius: 6px;
	}

	.complements-tab-container::-webkit-scrollbar-thumb {
		background: #CBD5E1;
		border-radius: 6px;
	}

	.complements-tab-container::-webkit-scrollbar-thumb:hover {
		background: #94A3B8;
	}

	.header-left {
		display: flex;
		align-items: center;
		gap: 24px;
		flex-wrap: wrap;
	}

	.header-title-row {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.brand-icon-box {
		width: 40px;
		height: 40px;
		border-radius: var(--radius-md, 10px);
		background: #FFF7ED;
		border: 1px solid #FFEDD5;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.header-heading-line {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.header-heading-line h1 {
		margin: 0;
		font-size: 19px;
		font-weight: 800;
		color: var(--neutral-text-primary, #0F172A);
		letter-spacing: -0.02em;
	}

	.badge-role {
		font-size: 11px;
		font-weight: 700;
		text-transform: uppercase;
		background: #F1F5F9;
		color: #475569;
		padding: 2px 8px;
		border-radius: 6px;
		letter-spacing: 0.04em;
	}

	.header-subtitle {
		margin: 2px 0 0 0;
		font-size: 12px;
		color: var(--neutral-text-secondary, #64748B);
	}

	.header-metrics {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.metric-chip {
		display: flex;
		align-items: center;
		gap: 6px;
		background: #F8FAFC;
		border: 1px solid #E2E8F0;
		padding: 5px 10px;
		border-radius: 8px;
		font-size: 12px;
	}

	.metric-chip.active-chip {
		background: #F0FDF4;
		border-color: #DCFCE7;
	}

	.dot-live {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: #10B981;
		box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
	}

	.metric-val {
		font-weight: 700;
		color: #0F172A;
	}

	.metric-val.text-emerald {
		color: #059669;
	}

	.metric-lbl {
		color: #64748B;
		font-weight: 500;
	}

	/* Ações do Header */
	.header-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	.btn-header-refresh {
		display: flex;
		align-items: center;
		gap: 6px;
		height: 36px;
		padding: 0 12px;
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 600;
		color: #475569;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-header-refresh:hover:not(:disabled) {
		background: #F8FAFC;
		border-color: #CBD5E1;
		color: #0F172A;
	}

	.btn-header-refresh.spinning :global(svg) {
		animation: spin 0.8s linear infinite;
	}

	@keyframes spin {
		100% { transform: rotate(360deg); }
	}

	.layout-dropdown-wrapper {
		position: relative;
	}

	.btn-layout-dropdown {
		display: flex;
		align-items: center;
		gap: 8px;
		height: 36px;
		padding: 0 12px;
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 600;
		color: #334155;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-layout-dropdown:hover {
		background: #F8FAFC;
		border-color: #CBD5E1;
	}

	.chevron-arrow {
		font-size: 10px;
		color: #94A3B8;
	}

	.layout-menu-popover {
		position: absolute;
		top: calc(100% + 6px);
		right: 0;
		width: 250px;
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		border-radius: 10px;
		box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
		padding: 6px;
		z-index: 100;
	}

	.menu-popover-title {
		padding: 8px 10px 4px 10px;
		font-size: 11px;
		font-weight: 700;
		color: #94A3B8;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.layout-menu-item {
		display: block;
		width: 100%;
		text-align: left;
		padding: 8px 10px;
		background: transparent;
		border: none;
		border-radius: 6px;
		cursor: pointer;
		transition: background 0.12s;
	}

	.layout-menu-item:hover {
		background: #F1F5F9;
	}

	.layout-menu-item.active {
		background: #FFF7ED;
	}

	.layout-menu-item.active .item-title {
		color: #FF6A00;
		font-weight: 700;
	}

	.item-title {
		font-size: 13px;
		font-weight: 600;
		color: #0F172A;
	}

	.item-sub {
		font-size: 11px;
		color: #64748B;
		margin-top: 2px;
	}

	.btn-import-ai {
		display: flex;
		align-items: center;
		gap: 6px;
		height: 36px;
		padding: 0 12px;
		background: #EEF2FF;
		border: 1px solid #E0E7FF;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 600;
		color: #4F46E5;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-import-ai:hover {
		background: #E0E7FF;
		border-color: #C7D2FE;
	}


	/* ========================================================
	   RESPONSIVIDADE DO CABEÇALHO
	   ======================================================== */
	@media (max-width: 1024px) {
		.menu-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.header-actions {
			width: 100%;
			flex-wrap: wrap;
		}
	}

	/* Switch / Toggle Component */
	.switch-control {
		position: relative;
		display: inline-block;
		width: 42px;
		height: 24px;
		flex-shrink: 0;
	}

	.switch-control input {
		opacity: 0;
		width: 0;
		height: 0;
	}

	.slider-switch {
		position: absolute;
		cursor: pointer;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: #CBD5E1;
		transition: .2s ease;
		border-radius: 24px;
	}

	.slider-switch:before {
		position: absolute;
		content: "";
		height: 18px;
		width: 18px;
		left: 3px;
		bottom: 3px;
		background-color: #FFFFFF;
		transition: .2s ease;
		border-radius: 50%;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	}

	.switch-control input:checked + .slider-switch {
		background-color: #FF6A00;
	}

	.switch-control input:checked + .slider-switch:before {
		transform: translateX(18px);
	}

	.switch-control:focus-within .slider-switch {
		box-shadow: 0 0 0 2px rgba(255, 106, 0, 0.3);
	}

	

	/* ========================================================
	   TABELA RÁPIDA - ETAPA 1: CONTAINER, HEADER & FILTROS
	   Padrão Design System Premium MataFome (Fase 4.1)
	   ======================================================== */
	.quick-table-page {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		width: 100%;
		padding: 20px 24px;
		gap: 16px;
		box-sizing: border-box;
		background: var(--neutral-bg, #F8FAFC);
		scrollbar-width: thin;
		scrollbar-color: #CBD5E1 transparent;
	}

	.quick-table-page::-webkit-scrollbar {
		width: 8px;
	}

	.quick-table-page::-webkit-scrollbar-track {
		background: #F1F5F9;
		border-radius: 6px;
	}

	.quick-table-page::-webkit-scrollbar-thumb {
		background: #CBD5E1;
		border-radius: 6px;
	}

	.quick-table-page::-webkit-scrollbar-thumb:hover {
		background: #94A3B8;
	}

	/* Card de Cabeçalho Executivo */
	.quick-table-header-card {
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-md, 10px);
		box-shadow: var(--shadow-card, 0 1px 3px 0 rgba(15, 23, 42, 0.06));
		padding: 16px 20px;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.quick-table-headline {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		flex-wrap: wrap;
	}

	.quick-headline-meta {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.quick-headline-title {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.quick-headline-title h3 {
		margin: 0;
		font-size: 16px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
		letter-spacing: -0.01em;
	}

	.quick-counter-pill {
		display: inline-flex;
		align-items: center;
		padding: 2px 8px;
		font-size: 11px;
		font-weight: 600;
		color: var(--text-secondary, #475569);
		background: var(--neutral-surface-hover, #F1F5F9);
		border-radius: var(--radius-full, 9999px);
	}

	.quick-headline-sub {
		margin: 0;
		font-size: 12px;
		color: var(--text-secondary, #64748B);
	}

	/* Métricas Compactas */
	.quick-metrics-row {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}

	.quick-stat-badge {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 12px;
		background: var(--neutral-surface-subtle, #F8FAFC);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 8px);
		font-size: 12px;
	}

	.quick-stat-badge .stat-label {
		color: var(--text-secondary, #64748B);
		font-weight: 500;
	}

	.quick-stat-badge .stat-val {
		color: var(--text-primary, #0F172A);
		font-weight: 700;
	}

	.quick-stat-badge.out-alert {
		background: var(--semantic-danger-soft, #FEF2F2);
		border-color: #FECACA;
	}

	.quick-stat-badge.out-alert .stat-label {
		color: #991B1B;
	}

	.quick-stat-badge.out-alert .stat-val {
		color: var(--semantic-danger, #DC2626);
	}

	/* Barra de Filtros Horizontais */
	.quick-filters-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		padding-top: 14px;
		border-top: 1px solid var(--neutral-border, #E2E8F0);
	}

	.quick-search-box {
		position: relative;
		display: flex;
		align-items: center;
		width: 300px;
		max-width: 100%;
	}

	.quick-search-box :global(svg) {
		position: absolute;
		left: 11px;
		pointer-events: none;
	}

	.quick-search-box input {
		width: 100%;
		height: 36px;
		padding: 0 32px 0 34px;
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 8px);
		font-size: 13px;
		font-family: inherit;
		color: var(--text-primary, #0F172A);
		transition: var(--transition-fast, 0.15s ease);
	}

	.quick-search-box input:focus {
		outline: none;
		border-color: var(--brand-primary, #FF6A00);
		box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.15);
	}

	.btn-clear-search {
		position: absolute;
		right: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		background: #E2E8F0;
		border: none;
		border-radius: 50%;
		color: #475569;
		cursor: pointer;
		padding: 0;
		transition: background 0.15s ease;
	}

	.btn-clear-search:hover {
		background: #CBD5E1;
		color: #0F172A;
	}

	/* Dropdowns de Filtro */
	.quick-filter-selects {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
	}

	.quick-select-group {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.quick-select-group label {
		margin: 0;
		font-size: 12px;
		font-weight: 600;
		color: var(--text-secondary, #475569);
		white-space: nowrap;
	}

	.quick-select-group select {
		height: 36px;
		padding: 0 28px 0 10px;
		background: var(--neutral-surface, #FFFFFF) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 8px);
		font-size: 12px;
		font-weight: 500;
		font-family: inherit;
		color: var(--text-primary, #0F172A);
		cursor: pointer;
		appearance: none;
		-webkit-appearance: none;
		transition: var(--transition-fast, 0.15s ease);
	}

	.quick-select-group select:focus {
		outline: none;
		border-color: var(--brand-primary, #FF6A00);
		box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.15);
	}

	@media (max-width: 900px) {
		.quick-filters-bar {
			flex-direction: column;
			align-items: stretch;
		}

		.quick-search-box {
			width: 100%;
		}

		.quick-filter-selects {
			width: 100%;
			justify-content: flex-start;
		}
	}


	/* ========================================================
	   TABELA RÁPIDA - ETAPA 2: TABELA, MINIATURAS & IDENTIDADE
	   Padrão Design System Premium MataFome (Fase 4.1)
	   ======================================================== */
	.quick-table-wrapper {
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-md, 10px);
		box-shadow: var(--shadow-card, 0 1px 3px 0 rgba(15, 23, 42, 0.06));
		overflow-x: auto;
		width: 100%;
		scrollbar-width: thin;
		scrollbar-color: #CBD5E1 transparent;
	}

	.quick-table-wrapper::-webkit-scrollbar {
		height: 6px;
	}

	.quick-table-wrapper::-webkit-scrollbar-thumb {
		background: #CBD5E1;
		border-radius: 4px;
	}

	/* Tabela de Produtos */
	.quick-products-table {
		width: 100%;
		border-collapse: collapse;
		text-align: left;
	}

	.quick-products-table thead th {
		background: var(--neutral-surface-subtle, #F8FAFC);
		padding: 12px 14px;
		font-size: 11px;
		font-weight: 700;
		color: var(--text-secondary, #64748B);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		white-space: nowrap;
	}

	.th-prod { min-width: 260px; }
	.th-cat { min-width: 130px; }
	.th-price { min-width: 155px; }
	.th-track { width: 120px; text-align: center; }
	.th-stock { min-width: 150px; }
	.th-status { width: 130px; text-align: center; }
	.th-actions { width: 110px; text-align: center; }

	.quick-row {
		border-bottom: 1px solid var(--neutral-border-subtle, #F1F5F9);
		transition: background var(--transition-fast, 0.15s ease);
	}

	.quick-row:last-child {
		border-bottom: none;
	}

	.quick-row:hover {
		background: var(--neutral-surface-subtle, #F8FAFC);
	}

	.quick-row.row-paused {
		opacity: 0.75;
		background: #FAFAFA;
	}

	.quick-products-table tbody td {
		padding: 10px 14px;
		vertical-align: middle;
	}

	.td-track, .td-status, .td-actions {
		text-align: center;
	}

	/* Miniatura e Identidade do Produto (TRAVA 44x44px) */
	.prod-identity {
		display: flex;
		align-items: center;
		gap: 12px;
		min-width: 0;
	}

	.prod-thumb-mini {
		width: 44px;
		height: 44px;
		min-width: 44px;
		border-radius: var(--radius-sm, 8px);
		background: var(--neutral-surface-hover, #F1F5F9);
		border: 1px solid var(--neutral-border, #E2E8F0);
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.prod-thumb-mini img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.prod-name-meta {
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 0;
		overflow: hidden;
	}

	.prod-name-text {
		font-size: 13px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.prod-desc-text {
		font-size: 11px;
		color: var(--text-secondary, #64748B);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 280px;
	}

	/* Tag da Categoria */
	.cat-chip {
		display: inline-flex;
		align-items: center;
		padding: 3px 8px;
		background: var(--neutral-surface-hover, #F1F5F9);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		font-size: 11px;
		font-weight: 600;
		color: var(--text-secondary, #475569);
		white-space: nowrap;
	}

	/* Estado Vazio */
	.quick-empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 48px 24px;
		text-align: center;
		gap: 8px;
	}

	.empty-icon-wrap {
		width: 56px;
		height: 56px;
		border-radius: 50%;
		background: var(--neutral-surface-hover, #F1F5F9);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 6px;
	}

	.quick-empty-state h4 {
		margin: 0;
		font-size: 15px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
	}

	.quick-empty-state p {
		margin: 0;
		font-size: 13px;
		color: var(--text-secondary, #64748B);
		max-width: 360px;
	}

	.btn-reset-filters {
		margin-top: 8px;
		padding: 6px 14px;
		background: var(--brand-primary-soft, #FFF7ED);
		border: 1px solid #FED7AA;
		border-radius: var(--radius-sm, 6px);
		font-size: 12px;
		font-weight: 600;
		color: var(--brand-primary, #FF6A00);
		cursor: pointer;
		transition: background 0.15s ease;
	}

	.btn-reset-filters:hover {
		background: #FFEDD5;
	}


	/* ========================================================
	   TABELA RÁPIDA - ETAPA 3: CONTROLES INLINE (PREÇO, ESTOQUE, STATUS)
	   Padrão Design System Premium MataFome (Fase 4.1)
	   ======================================================== */

	/* Inputs de Preço */
	.price-input-wrapper {
		position: relative;
		display: inline-flex;
		align-items: center;
		width: 105px;
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		padding: 0 6px 0 8px;
		transition: var(--transition-fast, 0.15s ease);
	}

	.price-input-wrapper:focus-within {
		border-color: var(--brand-primary, #FF6A00);
		box-shadow: 0 0 0 2px rgba(255, 106, 0, 0.18);
	}

	.currency-prefix {
		font-size: 11px;
		font-weight: 700;
		color: var(--text-muted, #94A3B8);
		user-select: none;
		margin-right: 4px;
	}

	.inline-price-input {
		width: 100%;
		height: 30px;
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		font-weight: 700;
		font-family: inherit;
		color: var(--text-primary, #0F172A);
		text-align: right;
		outline: none;
	}

	.inline-price-input.promo {
		color: #EA580C;
	}

	.inline-price-input::placeholder {
		color: #CBD5E1;
		font-weight: 400;
	}

	/* Célula Adaptativa de Pizzas (Broto e Grande) */
	.pizza-sizes-cell-stack {
		display: flex;
		flex-direction: column;
		gap: 5px;
		min-width: 150px;
	}

	.pizza-size-input-row {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.size-label-tag {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 24px;
		font-size: 10px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		border-radius: var(--radius-sm, 4px);
		flex-shrink: 0;
		user-select: none;
	}

	.size-label-tag.tag-broto {
		background: var(--brand-primary-soft, #FFF7ED);
		color: var(--brand-primary, #EA580C);
		border: 1px solid #FED7AA;
	}

	.size-label-tag.tag-grande {
		background: #EEF2FF;
		color: #4F46E5;
		border: 1px solid #E0E7FF;
	}

	.price-input-wrapper.price-input-mini {
		width: 95px;
		height: 26px;
		padding: 0 4px 0 6px;
	}

	.price-input-wrapper.price-input-mini .inline-price-input {
		height: 24px;
		font-size: 12px;
	}

	.price-input-wrapper.price-input-mini .currency-prefix {
		font-size: 10px;
		margin-right: 2px;
	}

	/* Switch de Controle de Estoque Mini */
	.switch-mini {
		transform: scale(0.85);
		margin: 0 auto;
		display: inline-block;
	}

	/* Stepper e Quantidade de Estoque */
	.stock-cell-content {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.stock-stepper-wrap {
		display: inline-flex;
		align-items: center;
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		background: var(--neutral-surface, #FFFFFF);
		overflow: hidden;
		height: 30px;
	}

	.btn-stepper-mini {
		width: 26px;
		height: 100%;
		border: none;
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-secondary, #475569);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: background var(--transition-fast, 0.15s ease);
		padding: 0;
	}

	.btn-stepper-mini:hover:not(:disabled) {
		background: #E2E8F0;
		color: var(--text-primary, #0F172A);
	}

	.btn-stepper-mini:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}

	.inline-stock-input {
		width: 44px;
		height: 100%;
		border: none;
		border-left: 1px solid var(--neutral-border, #E2E8F0);
		border-right: 1px solid var(--neutral-border, #E2E8F0);
		text-align: center;
		font-size: 13px;
		font-weight: 700;
		font-family: inherit;
		color: var(--text-primary, #0F172A);
		padding: 0 2px;
		outline: none;
		-moz-appearance: textfield;
	}

	.inline-stock-input::-webkit-outer-spin-button,
	.inline-stock-input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	.inline-stock-input.stock-zero {
		color: var(--semantic-danger, #DC2626);
	}

	.stock-badge-esgotado {
		display: inline-flex;
		align-items: center;
		padding: 2px 6px;
		background: var(--semantic-danger-soft, #FEF2F2);
		border: 1px solid #FECACA;
		border-radius: var(--radius-sm, 4px);
		font-size: 10px;
		font-weight: 700;
		color: var(--semantic-danger, #DC2626);
		text-transform: uppercase;
		letter-spacing: 0.03em;
		white-space: nowrap;
	}

	.stock-unlimited-pill {
		display: inline-flex;
		align-items: center;
		padding: 3px 8px;
		background: var(--neutral-surface-subtle, #F8FAFC);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		font-size: 11px;
		font-weight: 500;
		color: var(--text-muted, #94A3B8);
	}

	/* Botão de Status (Disponível / Pausado) */
	.quick-status-btn {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		height: 28px;
		padding: 0 10px;
		border-radius: var(--radius-full, 9999px);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: all var(--transition-fast, 0.15s ease);
		border: 1px solid transparent;
		white-space: nowrap;
	}

	.quick-status-btn.active {
		background: var(--semantic-success-soft, #ECFDF5);
		color: #065F46;
		border-color: #A7F3D0;
	}

	.quick-status-btn.active:hover {
		background: #D1FAE5;
	}

	.quick-status-btn.paused {
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-secondary, #475569);
		border-color: var(--neutral-border, #E2E8F0);
	}

	.quick-status-btn.paused:hover {
		background: #E2E8F0;
		color: var(--text-primary, #0F172A);
	}

	.status-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
	}

	.status-dot.green {
		background: var(--semantic-success, #059669);
		box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.2);
	}

	.status-dot.gray {
		background: #94A3B8;
	}

	/* Ações e Feedback ao Salvar */
	.row-actions-group {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		justify-content: center;
	}

	.saved-flash-pill {
		display: inline-flex;
		align-items: center;
		padding: 2px 7px;
		background: var(--semantic-success-soft, #ECFDF5);
		border: 1px solid #6EE7B7;
		border-radius: var(--radius-full, 9999px);
		font-size: 11px;
		font-weight: 700;
		color: #047857;
		animation: flashSaved 0.2s ease;
	}

	@keyframes flashSaved {
		from { opacity: 0; transform: scale(0.9); }
		to { opacity: 1; transform: scale(1); }
	}

	.btn-quick-edit-full {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		height: 28px;
		padding: 0 10px;
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		font-size: 12px;
		font-weight: 600;
		color: var(--text-secondary, #475569);
		cursor: pointer;
		transition: all var(--transition-fast, 0.15s ease);
	}

	.btn-quick-edit-full:hover {
		background: var(--neutral-surface-hover, #F1F5F9);
		border-color: #CBD5E1;
		color: var(--text-primary, #0F172A);
	}

	.row-just-saved {
		background-color: #F0FDF4 !important;
		transition: background-color 0.4s ease;
	}

</style>
